import { Box, IconButton, Table, Text } from "@chakra-ui/react";
import { Hint } from "@/ui/Hint";
import { LuX } from "react-icons/lu";
import { ProductState } from "@/core/products/components/ProductsTable";
import { resultingStock, toNumber } from "../utils/adjustmentStock";
import { NumberInputField, NumberInputRoot } from "@/ui/chakra/number-input";

interface AdjustmentProductsTableProps {
  products: ProductState[];
  setProducts: (products: ProductState[]) => void;
}

const headerProps = {
  fontSize: "xs",
  fontWeight: "semibold",
  letterSpacing: "wider",
  textTransform: "uppercase",
  color: "fg.muted",
  whiteSpace: "nowrap",
} as const;

const AdjustmentProductsTable: React.FC<AdjustmentProductsTableProps> = ({
  setProducts,
  products,
}) => {
  const handleUpdateStockIncoment = (
    productId: string,
    newStockIncoment: number | string,
  ) => {
    setProducts(
      products.map((product) =>
        product.data.id === productId
          ? { ...product, amount: newStockIncoment }
          : product,
      ),
    );
  };

  const handleRemoveProduct = (productId: string) => {
    setProducts(products.filter((product) => product.data.id !== productId));
  };
  return (
    <Box borderWidth="1px" borderColor="border" rounded="lg" overflow="hidden">
      <Hint rounded="none" borderBottomWidth="1px" borderColor="border">
        Escribe la cantidad a sumar. Para descontar usa un número negativo, por
        ejemplo <b>-5</b>. La columna «Stock resultante» muestra cómo quedaría.
      </Hint>
      <Table.ScrollArea maxH="320px">
        <Table.Root size="sm" variant="line" stickyHeader>
          <Table.Header bg="bg.subtle">
            <Table.Row>
              <Table.ColumnHeader {...headerProps}>Código</Table.ColumnHeader>
              <Table.ColumnHeader {...headerProps}>
                Descripción
              </Table.ColumnHeader>
              <Table.ColumnHeader {...headerProps} textAlign="end">
                Stock actual
              </Table.ColumnHeader>
              <Table.ColumnHeader {...headerProps} textAlign="end">
                Cantidad a reajustar
              </Table.ColumnHeader>
              <Table.ColumnHeader {...headerProps} textAlign="end">
                Stock resultante
              </Table.ColumnHeader>
              <Table.ColumnHeader width="1%" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {products.map((product) => {
              const code =
                product.data.externalProduct?.code ||
                product.data.erpProduct?.erpCode;
              const delta = toNumber(product.amount);
              const result = resultingStock(product);
              const negative = result < 0;
              return (
                <Table.Row key={product.data.id}>
                  <Table.Cell
                    fontWeight="semibold"
                    maxW="140px"
                    truncate
                    title={code}
                  >
                    {code}
                  </Table.Cell>
                  <Table.Cell minW="200px">
                    {product.data.description}
                  </Table.Cell>
                  <Table.Cell textAlign="end">{product.data.stock}</Table.Cell>
                  <Table.Cell textAlign="end">
                    <NumberInputRoot
                      size="sm"
                      w="140px"
                      ml="auto"
                      min={-1000}
                      max={1000}
                      clampValueOnBlur={false}
                      invalid={negative}
                      formatOptions={{ maximumFractionDigits: 2 }}
                      value={String(product.amount ?? 0)}
                      onValueChange={(e) => {
                        if (e.value === "-" || e.value === "") {
                          handleUpdateStockIncoment(product.data.id, e.value);
                          return;
                        }
                        if (!isNaN(e.valueAsNumber)) {
                          handleUpdateStockIncoment(
                            product.data.id,
                            e.valueAsNumber,
                          );
                        }
                      }}
                    >
                      <NumberInputField
                        aria-label="Cantidad a reajustar"
                        placeholder="+10 o -5"
                      />
                    </NumberInputRoot>
                  </Table.Cell>
                  <Table.Cell textAlign="end" whiteSpace="nowrap">
                    <Text
                      as="span"
                      fontWeight="semibold"
                      color={
                        negative
                          ? "fg.error"
                          : delta > 0
                            ? "green.600"
                            : delta < 0
                              ? "orange.600"
                              : "fg.muted"
                      }
                    >
                      {result}
                    </Text>
                    {negative && (
                      <Text fontSize="xs" color="fg.error">
                        No puede quedar bajo 0
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    <IconButton
                      aria-label="Quitar producto"
                      size="sm"
                      variant="ghost"
                      color="fg.muted"
                      onClick={() => handleRemoveProduct(product.data.id)}
                    >
                      <LuX />
                    </IconButton>
                  </Table.Cell>
                </Table.Row>
              );
            })}
            {products.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={5} textAlign="center" py={8}>
                  <Text color="fg.muted" fontSize="sm">
                    Aún no hay productos. Busca uno para agregarlo al reajuste.
                  </Text>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      </Table.ScrollArea>
    </Box>
  );
};

export default AdjustmentProductsTable;
